/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */
import { ConvivaSession } from '../ConvivaSession';
import {
  AdBreakInfo,
  AdComposedPlayer,
  AdContentTimePosition,
  AdInfo,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  Player,
  PlayerContext,
  SEEKING_STATES,
  VASTProperties,
} from '@quickplay/player-interface';
import { createLogger, Error, ErrorCodes, Logger, LoggerLevel } from '@quickplay/foundation';
import { ConvivaInitializationData } from '../model/ConvivaInitializationData';
import {
  DEFAULT_AD_ASSET_NAME,
  DEFAULT_AD_CREATIVE_ID,
  DEFAULT_AD_FIRST_AD_SYSTEM,
  DEFAULT_AD_FIRST_CREATIVE_ID,
  DEFAULT_AD_ID,
  DEFAULT_AD_IS_SLATE,
  DEFAULT_AD_MEDIA_FILE_API_FRAMEWORK,
  DEFAULT_AD_STITCHER,
  DEFAULT_AD_SYSTEM,
  getConnectionType,
} from './ConvivaUtil';
import {
  AdAnalytics,
  ConvivaConstants,
  ConvivaDeviceMetadata,
  ConvivaMetadata,
  ConvivaPlayerInfo,
  valueof,
  VideoAnalytics,
} from './conviva-core-sdk';
import { AdTechnology } from './AdTechnology';
import { AdMetadata } from '../model/AdMetadata';

declare const Conviva: any;

const TAG_NAME = 'FL_CONVIVA_SESSION';

export class ConvivaSessionImpl implements ConvivaSession {
  private internalPlayer: Player | undefined;
  private logger: Logger;
  private videoAnalytics: VideoAnalytics | undefined;
  private adAnalytics: AdAnalytics | undefined;
  private readonly deviceMetadata: ConvivaDeviceMetadata | undefined;
  private readonly contentPlayerInfo: ConvivaPlayerInfo | undefined;
  private readonly adPlayerInfo: ConvivaPlayerInfo | undefined;
  private metadata?: ConvivaMetadata;
  private adMetadata?: AdMetadata;
  private isAdBreakStartReported?: boolean;
  private isAdBreakEndReported?: boolean;
  private bufferingState: BUFFERING_STATES = BUFFERING_STATES.INACTIVE;

  constructor(
    private initializationData: ConvivaInitializationData,
    deviceMetadata?: ConvivaDeviceMetadata,
    contentPlayerInfo?: ConvivaPlayerInfo,
    adPlayerInfo?: ConvivaPlayerInfo,
    logger?: Logger,
  ) {
    this.internalPlayer = undefined;
    this.isAdBreakStartReported = false;
    this.isAdBreakEndReported = false;
    this.deviceMetadata = deviceMetadata;
    this.contentPlayerInfo = contentPlayerInfo;
    this.adPlayerInfo = adPlayerInfo;
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
  }

  initialize(): void {
    if (this.initializationData.debug) {
      if (!this.initializationData.gatewayUrl) {
        this.logger.log(`[Conviva] Gateway URL is missing. Debug session not initialized.`);
        return;
      }

      const settings = {
        [Conviva.Constants.GATEWAY_URL]: this.initializationData.gatewayUrl,
        [Conviva.Constants.LOG_LEVEL]: Conviva.Constants.LogLevel.DEBUG,
      };

      Conviva.Analytics.init(this.initializationData.customerKey, null, settings);
    } else {
      Conviva.Analytics.init(this.initializationData.customerKey, null);
    }

    Conviva.Analytics.setDeviceMetadata(this.deviceMetadata);

    // video analytics
    this.videoAnalytics = Conviva.Analytics.buildVideoAnalytics();

    // ad analytics
    this.adAnalytics = Conviva.Analytics.buildAdAnalytics(this.videoAnalytics) as AdAnalytics;

    this.logger.log(`[Conviva] Session initialized`, this.initializationData);
  }

  reportPlaybackRequested(metadata: ConvivaMetadata, adMetadata?: AdMetadata): void {
    if (!this.isInitialized() || !this.videoAnalytics) {
      this.logger.log(`[Conviva][Error] Session is not initialized`);
      return;
    }

    this.metadata = metadata;
    this.adMetadata = adMetadata;

    this.videoAnalytics.reportPlaybackRequested(this.metadata);
    Conviva.Analytics.reportDeviceMetric(Conviva.Constants.Network.CONNECTION_TYPE, getConnectionType());
  }

  attachPlayer(player: Player): void {
    if (!this.isInitialized() || !this.videoAnalytics) {
      this.logger.log(`[Conviva][Error] Session is not initialized`);
      return;
    }

    this.internalPlayer = player;
    const contentPlayerInfo = this.getPlayerInfo(this.contentPlayerInfo, player);
    this.videoAnalytics?.setPlayerInfo(contentPlayerInfo);

    this.subscribeToInternalPlayerEvents(this.internalPlayer);

    this.logger.log(`[Conviva] Player attached`);
  }

  setContentMetadata(metadata: ConvivaMetadata): void {
    if (!this.videoAnalytics) {
      return;
    }

    this.metadata = { ...this.metadata, ...metadata };

    this.videoAnalytics.setContentInfo(metadata);
  }

  reportError(error: Error): void {
    this.onError(error);
  }

  reportEvent(eventType: valueof<ConvivaConstants['Events']>, detail?: object): void {
    this.reportCustomEvent(eventType, detail);
  }

  reportPlaybackMetricEvent(
    metricKey: valueof<ConvivaConstants['Playback']>,
    metricValue?: valueof<ConvivaConstants['PlayerState']> | number | string,
    metricValue2?: valueof<ConvivaConstants['PlayerState']> | number | string,
  ): void {
    if (!this.videoAnalytics) {
      return;
    }
    this.reportPlaybackMetric(metricKey, metricValue, metricValue2);
  }

  /**
   * Builds player information based on the context.
   * @returns key value pair.
   */
  private getPlayerInfo = (playerInfo?: ConvivaPlayerInfo, player?: Player): { [key: string]: any } => {
    return {
      [Conviva.Constants.FRAMEWORK_NAME]: playerInfo?.['Conviva.frameworkName'] || player?.internalPlayerName || '',
      [Conviva.Constants.FRAMEWORK_VERSION]:
        playerInfo?.['Conviva.frameworkVersion'] || player?.internalPlayerVersion || '',
    };
  };

  private reportPlaybackMetric(
    metricKey: valueof<ConvivaConstants['Playback']>,
    metricValue?: valueof<ConvivaConstants['PlayerState']> | number | string,
    metricValue2?: valueof<ConvivaConstants['PlayerState']> | number | string,
  ): void {
    if (!this.videoAnalytics) {
      return;
    }
    this.videoAnalytics.reportPlaybackMetric(metricKey, metricValue, metricValue2);
  }

  reportAdMetricEvent(
    metricKey: valueof<ConvivaConstants['Playback']>,
    metricValue?: valueof<ConvivaConstants['PlayerState']> | number | string,
    metricValue2?: valueof<ConvivaConstants['PlayerState']> | number | string,
  ): void {
    if (!this.adAnalytics) {
      return;
    }
    this.reportAdMetric(metricKey, metricValue, metricValue2);
  }

  private reportAdMetric(
    metricKey: valueof<ConvivaConstants['Playback']>,
    metricValue?: valueof<ConvivaConstants['PlayerState']> | number | string,
    metricValue2?: valueof<ConvivaConstants['PlayerState']> | number | string,
  ): void {
    if (!this.adAnalytics) {
      return;
    }
    this.adAnalytics.reportAdMetric(metricKey, metricValue, metricValue2);
  }

  reportCustomEvent(eventType: string, detail?: object): void {
    if (!this.videoAnalytics) {
      return;
    }
    this.videoAnalytics.reportPlaybackEvent(eventType, detail);
  }

  reportAppBackgrounded(): void {
    Conviva.Analytics.reportAppBackgrounded();
  }

  reportAppForegrounded(): void {
    Conviva.Analytics.reportAppForegrounded();
  }
  /**
   * Subscribe to player events.
   * @param player
   */
  private subscribeToInternalPlayerEvents = (player?: Player): void => {
    if (!player) {
      this.logger.log(`[Conviva][Error] Cannot subscribe to undefined player events`);
      return;
    }

    player.subscribe('bufferingstatechanged', this.onBufferingStateChanged);
    player.subscribe('progressupdate', this.onProgressUpdate);
    player.subscribe('streamended', this.onStreamEnded);
    player.subscribe('seekingstatechanged', this.onSeekingStateChanged);
    player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    player.subscribe('bandwidthchanged', this.onBandwidthChanged);
    player.subscribe('adbreakstart', this.onAdBreakStart);
    player.subscribe('adbreakend', this.onAdBreakEnd);
    player.subscribe('adstart', this.onAdStart);
    player.subscribe('adend', this.onAdEnd);
    player.subscribe('adprogressupdate', this.onAdProgressUpdate);
    player.subscribe('error', this.onError);
    player.subscribe('aderror', this.onAdError);
  };

  /**
   * Unsubscribe from player events.
   * @param player
   */
  private unsubscribeFromInternalPlayerEvents = (player?: Player): void => {
    if (!player) {
      this.logger.log(`[Conviva][Error] Cannot unsubscribe from undefined player events`);
      return;
    }

    player.unsubscribe('bufferingstatechanged', this.onBufferingStateChanged);
    player.unsubscribe('progressupdate', this.onProgressUpdate);
    player.unsubscribe('streamended', this.onStreamEnded);
    player.unsubscribe('seekingstatechanged', this.onSeekingStateChanged);
    player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    player.unsubscribe('bandwidthchanged', this.onBandwidthChanged);
    player.unsubscribe('adbreakstart', this.onAdBreakStart);
    player.unsubscribe('adbreakend', this.onAdBreakEnd);
    player.unsubscribe('adstart', this.onAdStart);
    player.unsubscribe('adend', this.onAdEnd);
    player.unsubscribe('adprogressupdate', this.onAdProgressUpdate);
    player.unsubscribe('error', this.onError);
    player.unsubscribe('aderror', this.onAdError);
  };

  private onBufferingStateChanged = (bufferingState: BUFFERING_STATES): void => {
    if (!this.videoAnalytics) {
      return;
    }

    this.logger.log(`[Conviva][PlayerEvent] bufferingstatechanged`, bufferingState);

    switch (bufferingState) {
      case BUFFERING_STATES.ACTIVE: {
        this.bufferingState = BUFFERING_STATES.ACTIVE;
        this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.BUFFERING);
        break;
      }
      case BUFFERING_STATES.INACTIVE: {
        this.bufferingState = BUFFERING_STATES.INACTIVE;
        if (this.internalPlayer && this.internalPlayer.playerState === PLAYBACK_STATES.PAUSED) {
          this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PAUSED);
        } else {
          this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PLAYING);
        }
        break;
      }
    }
  };

  private onProgressUpdate = (): void => {
    if (!this.internalPlayer || !this.internalPlayer.duration || !this.videoAnalytics) {
      return;
    }

    const contentInfo: ConvivaMetadata = {};
    if (!this.internalPlayer.isLive) {
      contentInfo['Conviva.duration'] = this.internalPlayer.duration;
    }

    if (Object.keys(contentInfo).length) {
      this.videoAnalytics.setContentInfo(contentInfo);
    }

    this.reportPlaybackMetric(
      Conviva.Constants.Playback.DROPPED_FRAMES_TOTAL,
      this.internalPlayer.playbackStatistics?.droppedFrames,
    );

    if (!this.internalPlayer.isLive) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.PLAY_HEAD_TIME,
        (this.internalPlayer.currentTime || 0) * 1_000,
      );
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.RENDERED_FRAMERATE,
        this.internalPlayer.playbackStatistics?.framerate,
      );
    }
  };

  private onStreamEnded = (): void => {
    if (!this.videoAnalytics) {
      return;
    }

    this.logger.log(`[Conviva][PlayerEvent] streamended`);
    this.videoAnalytics.reportPlaybackEnded();
  };

  private onSeekingStateChanged = (seekingState: SEEKING_STATES): void => {
    if (!this.videoAnalytics) {
      return;
    }

    this.logger.log(`[Conviva][PlayerEvent] seekingstatechanged`, seekingState);

    switch (seekingState) {
      case SEEKING_STATES.ACTIVE: {
        this.reportPlaybackMetric(Conviva.Constants.Playback.SEEK_STARTED);
        break;
      }
      case SEEKING_STATES.INACTIVE: {
        this.reportPlaybackMetric(Conviva.Constants.Playback.SEEK_ENDED);
        break;
      }
    }
  };

  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    if (!this.videoAnalytics) {
      return;
    }
    this.logger.log(`[Conviva][PlayerEvent] playbackstatechanged`, playbackState);

    switch (playbackState) {
      case PLAYBACK_STATES.LOADING: {
        // no-op
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        // no-op
        break;
      }
      case PLAYBACK_STATES.STARTED: {
        this.onPlaybackStarted();
        break;
      }
      case PLAYBACK_STATES.PAUSED: {
        this.onPlaybackPaused();
        break;
      }
      case PLAYBACK_STATES.STOPPED: {
        this.onPlaybackStopped();
        break;
      }
      case PLAYBACK_STATES.IDLE: {
        this.onPlaybackIdle();
        break;
      }
    }
  };

  private onPlaybackStarted = (): void => {
    if (!this.isMainContext(this.internalPlayer)) {
      this.reportAdMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PLAYING);
      return;
    }

    if (this.metadata) {
      this.metadata[Conviva.Constants.IS_LIVE] = this.isLive(this.internalPlayer);
      this.setContentMetadata(this.metadata);
    } else {
      this.setContentMetadata({
        [Conviva.Constants.IS_LIVE]: this.isLive(this.internalPlayer),
      });
    }

    // Note:
    // During the start of the playback when the player moves to started state and the buffering state is still active.
    // For this scenario, we should report the playerState as PLAYING to conviva unless the buffering ends.
    if (this.bufferingState !== BUFFERING_STATES.ACTIVE) {
      this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PLAYING);
    }
    if (this.internalPlayer && this.internalPlayer.playbackStatistics) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.BITRATE,
        this.internalPlayer.playbackStatistics.variantBitrate / 1000,
      );
      if (this.internalPlayer.playbackStatistics.framerate) {
        this.reportPlaybackMetric(
          Conviva.Constants.Playback.RENDERED_FRAMERATE,
          this.internalPlayer.playbackStatistics.framerate,
        );
      }
    }
  };

  private onPlaybackPaused = (): void => {
    if (!this.isMainContext(this.internalPlayer)) {
      this.reportAdMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PAUSED);
      return;
    }

    this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PAUSED);

    if (!this.internalPlayer?.isLive) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.PLAY_HEAD_TIME,
        (this.internalPlayer?.currentTime || 0) * 1_000,
      );
    }
  };

  private onPlaybackStopped = (): void => {
    this.reportPlaybackMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.STOPPED);

    if (!this.internalPlayer?.isLive) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.PLAY_HEAD_TIME,
        (this.internalPlayer?.currentTime || 0) * 1_000,
      );
    }
  };

  private onPlaybackIdle = (): void => {
    if (!this.internalPlayer?.isLive) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.PLAY_HEAD_TIME,
        (this.internalPlayer?.currentTime || 0) * 1_000,
      );
    }
    this.videoAnalytics?.reportPlaybackEnded();

    this.unsubscribeFromInternalPlayerEvents(this.internalPlayer);
  };

  private onBandwidthChanged = (): void => {
    if (!this.videoAnalytics) {
      return;
    }

    if (this.internalPlayer && this.internalPlayer.playbackStatistics) {
      this.logger.log(
        `[Conviva][PlayerEvent] bandwidthchanged`,
        this.internalPlayer.playbackStatistics.variantBitrate / 1000,
      );

      this.reportPlaybackMetric(
        Conviva.Constants.Playback.BITRATE,
        this.internalPlayer.playbackStatistics.variantBitrate / 1000,
      );
    } else {
      this.logger.log(`[Conviva][PlayerEvent] bandwidthchanged - Bitrate is not available`);
    }
  };

  private onAdBreakStart = (adBreakInfo?: AdBreakInfo): void => {
    if (!this.videoAnalytics || this.isAdBreakStartReported) {
      return;
    }

    // Note:
    // If adBreakStart is reported to conviva, set isAdBreakStartReported to true to avoid the duplicate reports in case of play/load ad errors are reported.
    this.isAdBreakStartReported = true;

    // Note:
    // Reset the isAdBreakEndReported to false to reports the consecutive adBreakEnd to conviva properly.
    this.isAdBreakEndReported = false;
    this.logger.log(`[Conviva][PlayerEvent] adbreakstart`, adBreakInfo);
    if (this.adMetadata && this.adMetadata.adTechnology === AdTechnology.SERVER_SIDE) {
      this.videoAnalytics.reportAdBreakStarted(
        Conviva.Constants.AdType.SERVER_SIDE,
        Conviva.Constants.AdPlayer.CONTENT,
      );
    } else {
      this.videoAnalytics.reportAdBreakStarted(
        Conviva.Constants.AdType.CLIENT_SIDE,
        Conviva.Constants.AdPlayer.SEPARATE,
      );
    }
  };

  private onAdBreakEnd = (adBreakInfo?: AdBreakInfo): void => {
    if (!this.videoAnalytics || this.isAdBreakEndReported) {
      return;
    }

    // Note:
    // If adBreakEnd is reported to conviva, set isAdBreakEndReported to true to avoid the duplicate reports in case of play/load ad errors are reported.
    this.isAdBreakEndReported = true;

    // Note:
    // Reset the isAdBreakStartReported to false to reports the consecutive adBreakStart to conviva properly.
    this.isAdBreakStartReported = false;
    this.logger.log(`[Conviva][PlayerEvent] adbreakend`, adBreakInfo);
    this.videoAnalytics.reportAdBreakEnded();
  };

  private onAdStart = (adInfo: AdInfo): void => {
    if (!this.adAnalytics) {
      return;
    }
    this.logger.log(`[Conviva][PlayerEvent] adstart`, adInfo);

    const adPlayerInfo = this.getPlayerInfo(this.adPlayerInfo, this.internalPlayer);
    this.adAnalytics.setAdPlayerInfo(adPlayerInfo);
    this.adAnalytics.reportAdStarted(this.buildAdMetadata(adInfo));
    if (adInfo.vastProperties && adInfo.vastProperties.bitrate) {
      this.adAnalytics.reportAdMetric(Conviva.Constants.Playback.BITRATE, adInfo.vastProperties.bitrate);
    }

    if (this.internalPlayer?.playbackStatistics?.framerate) {
      this.reportPlaybackMetric(
        Conviva.Constants.Playback.RENDERED_FRAMERATE,
        this.internalPlayer.playbackStatistics.framerate,
      );
      this.reportAdMetric(
        Conviva.Constants.Playback.RENDERED_FRAMERATE,
        this.internalPlayer.playbackStatistics.framerate,
      );
    }
  };

  private onAdEnd = (adInfo: AdInfo): void => {
    if (!this.adAnalytics) {
      return;
    }
    this.logger.log(`[Conviva][PlayerEvent] adend`, adInfo);
    this.adAnalytics.reportAdEnded();
  };

  private onAdProgressUpdate = (activeAdInfo: AdInfo, progress: number): void => {
    if (!this.adAnalytics) {
      return;
    }

    this.logger.log(`[Conviva][PlayerEvent] adprogressupdate`, activeAdInfo, progress);
    this.adAnalytics.reportAdMetric(Conviva.Constants.Playback.PLAYER_STATE, Conviva.Constants.PlayerState.PLAYING);
    this.adAnalytics.reportAdMetric(
      Conviva.Constants.Playback.DROPPED_FRAMES_TOTAL,
      this.internalPlayer?.playbackStatistics?.droppedFrames,
    );
  };

  private onError = (error: Error): void => {
    if (!error || !this.videoAnalytics) {
      return;
    }

    this.logger.log(`[Conviva][PlayerEvent] error`, error);
    let errorMessage = `${error}`;
    if (
      error.doesBelongToCategory &&
      error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER) &&
      error.internalError
    ) {
      errorMessage = `${error.internalError.errorMessage} (${error.internalError.errorCode})`;
    } else if (error.hexErrorCode) {
      errorMessage = `${error.errorMessage} (${error.hexErrorCode})`;
    } else if (error.errorCode !== undefined) {
      errorMessage = `${error.errorMessage} (${error.errorCode})`;
    }

    if (!this.isMainContext(this.internalPlayer)) {
      this.onAdError(errorMessage);
    }

    this.videoAnalytics.reportPlaybackFailed(errorMessage);
  };

  private onAdError = (adError: any): void => {
    if (!adError || !this.adAnalytics) {
      return;
    }
    // Note:
    // CSAI ads will throw adLoadError and adPlayError. For adLoadErrors, the adBreakStart and adBreakEnd is not emitted.
    // To report the both errors and to avoid the duplicate reports to the conviva, we have to check whether adBreakStart is already get reported.
    // If adBreadStart is reported, we should not report the adBreadStart again to conviva.
    if (!this.isAdBreakStartReported) {
      this.onAdBreakStart();
    }

    let errorMessage = JSON.stringify(adError);
    const isIMAAdError = adError?.getMessage && adError?.getErrorCode;
    if (isIMAAdError) {
      errorMessage = `${adError?.getMessage()} (${adError?.getErrorCode()})`;
    }
    this.logger.log(`[Conviva][AdEvent] error`, adError);
    this.adAnalytics.reportAdFailed(errorMessage, this.buildAdMetadata());

    // Note:
    // CSAI ads will throw adLoadError and adPlayError. For adLoadErrors, the adBreakStart and adBreakEnd is not emitted.
    // To report the both errors and to avoid the duplicate reports to the conviva, we have to check whether adBreakEnd is already get reported.
    // If adBreadEnd is reported, we should not report the adBreakEnd again to conviva.
    if (!this.isAdBreakEndReported) {
      this.onAdBreakEnd();
    }
  };

  /**
   * This guard will check whether an instance of Player implements
   * the AdComposedPlayer interface or not.
   * @param player the Player instance to check
   */
  private instanceOfAdComposedPlayer = (player: Player): player is AdComposedPlayer => {
    return (player as AdComposedPlayer).playerContext !== undefined;
  };

  private isMainContext = (player: Player | undefined): boolean => {
    if (player && this.instanceOfAdComposedPlayer(player)) {
      return player.playerContext === PlayerContext.MAIN ? true : false;
    }
    return true;
  };

  /**
   * Builds ad metadata object from ad info.
   * @param adInfo
   */
  private buildAdMetadata = (adInfo?: AdInfo): ConvivaMetadata => {
    const adMetadata: ConvivaMetadata = {};
    if (this.adMetadata) {
      adMetadata['c3.ad.adStitcher'] = this.adMetadata.adStitcher ?? DEFAULT_AD_STITCHER;
      adMetadata['c3.ad.mediaFileApiFramework'] =
        this.adMetadata.adMediaFileApiFramework ?? DEFAULT_AD_MEDIA_FILE_API_FRAMEWORK;
      adMetadata['c3.ad.isSlate'] = this.adMetadata.isSlate ?? DEFAULT_AD_IS_SLATE;
      adMetadata['c3.ad.technology'] =
        this.adMetadata.adTechnology && this.adMetadata.adTechnology === AdTechnology.SERVER_SIDE
          ? Conviva.Constants.AdType.SERVER_SIDE
          : Conviva.Constants.AdType.CLIENT_SIDE;
    }
    if (adInfo) {
      this.setVastProperties(adMetadata, adInfo.vastProperties);
      adMetadata[Conviva.Constants.IS_LIVE] =
        this.metadata && this.metadata[Conviva.Constants.IS_LIVE]
          ? this.metadata[Conviva.Constants.IS_LIVE]
          : this.isLive(this.internalPlayer);
      adMetadata['Conviva.duration'] = adInfo.durationMs ? adInfo.durationMs / 1000 : 0;
      adMetadata['c3.ad.id'] = adInfo.adID;
      adMetadata['c3.ad.position'] = this.toC3AdPosition(adInfo.adBreakInfo);
      adMetadata['c3.ad.firstAdSystem'] = this.toC3AdFirstAdSystem(adInfo);
      adMetadata['c3.ad.firstAdId'] = this.toC3AdFirstAdId(adInfo);
      adMetadata['c3.ad.firstCreativeId'] = this.toC3AdFirstCreavtiveId(adInfo);
    }
    this.setAdStreamUrl(adMetadata, adInfo);

    return adMetadata;
  };

  private setVastProperties(adMetadata: ConvivaMetadata, vastProperties?: VASTProperties): void {
    if (vastProperties) {
      adMetadata[Conviva.Constants.ASSET_NAME] = vastProperties.adTitle ?? DEFAULT_AD_ASSET_NAME;
      adMetadata['c3.ad.system'] = vastProperties.adSystem ?? DEFAULT_AD_SYSTEM;
      adMetadata['c3.ad.creativeId'] = vastProperties.creativeId ?? DEFAULT_AD_CREATIVE_ID;
    }
  }

  private setAdStreamUrl(adMetadata: ConvivaMetadata, adInfo?: AdInfo): void {
    if (adInfo && adInfo.mediaUrl) {
      adMetadata[Conviva.Constants.STREAM_URL] = adInfo.mediaUrl;
    } else if (this.adMetadata && this.adMetadata.adMediaUrl) {
      adMetadata[Conviva.Constants.STREAM_URL] = this.adMetadata.adMediaUrl;
    } else if (
      this.metadata &&
      this.metadata[Conviva.Constants.STREAM_URL] &&
      this.adMetadata?.adTechnology === AdTechnology.SERVER_SIDE
    ) {
      adMetadata[Conviva.Constants.STREAM_URL] = this.metadata[Conviva.Constants.STREAM_URL];
    }
  }

  private toC3AdFirstAdSystem(adInfo: AdInfo): string {
    if (adInfo.vastProperties && adInfo.vastProperties.adSystem && adInfo.sequence === 1) {
      return adInfo.vastProperties.adSystem;
    }
    return DEFAULT_AD_FIRST_AD_SYSTEM;
  }

  private toC3AdFirstCreavtiveId(adInfo: AdInfo): string {
    if (adInfo.vastProperties && adInfo.vastProperties.creativeId && adInfo.sequence === 1) {
      return adInfo.vastProperties.creativeId;
    }
    return DEFAULT_AD_FIRST_CREATIVE_ID;
  }

  private toC3AdFirstAdId(adInfo: AdInfo): string {
    if (adInfo.sequence === 1) {
      return adInfo.adID;
    }
    return DEFAULT_AD_ID;
  }
  /**
   * Translates from FL ad break time position value to Conviva ad break
   * time position.
   * @param adBreakInfo
   */
  private toC3AdPosition = (adBreakInfo?: AdBreakInfo): any => {
    if (!adBreakInfo) {
      return Conviva.Constants.AdPosition.PREROLL;
    }

    switch (adBreakInfo.contentTimePosition) {
      case AdContentTimePosition.MIDROLL:
        return Conviva.Constants.AdPosition.MIDROLL;
      case AdContentTimePosition.POSTROLL:
        return Conviva.Constants.AdPosition.POSTROLL;
      case AdContentTimePosition.PREROLL:
      default:
        return Conviva.Constants.AdPosition.PREROLL;
    }
  };

  /**
   * Returns whether playback is live, VOD or unknown.
   * @param player the player instance
   */
  private isLive = (player?: Player): any => {
    if (!player) {
      return Conviva.Constants.StreamType.UNKNOWN;
    }

    if (player.isLive) {
      return Conviva.Constants.StreamType.LIVE;
    }

    return Conviva.Constants.StreamType.VOD;
  };

  isInitialized = (): boolean => {
    return !!this.videoAnalytics;
  };

  /**
   * Releases all Conviva resources.
   */
  destroy = (): void => {
    if (this.videoAnalytics) {
      this.videoAnalytics.release();
      this.videoAnalytics = undefined;
    }

    if (this.adAnalytics) {
      this.adAnalytics.release();
      this.adAnalytics = undefined;
    }

    Conviva.Analytics.release();
  };
}
